/**
 * Qué es este sistema y cómo se explica, dentro de la propia app.
 *
 * Está escrito para quien lo lee documentándose: un padre, una madre, una
 * tutora, alguien del equipo que acompaña a un niño. No se dirige a quien montó
 * la casa ni le da instrucciones; describe qué es esto, cómo se llama y qué
 * ocurre cuando se explica. El lector llega con una pregunta, no con una tarea.
 *
 * Vive aparte de `es.ts` por una razón concreta: el guardián de lenguaje limita
 * cada cadena de la interfaz a 120 caracteres, y con razón — esas líneas se leen
 * en voz alta a un niño. Esto no es interfaz, es un documento para un adulto, y
 * un párrafo no cabe en 120 caracteres sin quedar troceado.
 *
 * Lo que sí se le sigue aplicando es el resto del guardián: nada de etiquetas,
 * nada de «casi», nada de vergüenza, nada de gritos. Está en `porQue.test.ts`,
 * y usa exactamente los mismos patrones. Separarlo del fichero de cadenas no es
 * sacarlo de la regla; es dejarlo bajo la parte de la regla que le corresponde.
 *
 * Y está dentro de la app, y no en un enlace, porque un enlace no se abre en
 * modo avión y porque enseñárselo a alguien no debería obligar a compartir una
 * dirección de internet.
 */

export interface Bloque {
  readonly titulo: string
  readonly parrafos: readonly string[]
}

export interface Situacion {
  readonly quien: string
  readonly decir: string
  readonly evitar: string
}

export const PORQUE = {
  titulo: 'Qué es este sistema y cómo se explica',
  entradilla:
    'Esta app reparte las tareas de una casa entre los niños que viven en ella, las ordena en rutinas y misiones, y paga algunas con monedas que se cambian por tiempo de pantalla y otras cosas. Lo que sigue explica de dónde viene ese diseño y cómo se nombra.',

  frase: {
    titulo: 'En una frase',
    texto: 'Es un andamio para la autonomía.',
    detalle:
      'La economía de fichas es el andamio, y el diseño entero está pensado para retirarlo: las rutinas no dan monedas nunca, y las misiones dan cada vez menos según se dominan, hasta cero. Si el sistema funciona, deja de hacer falta. No es un efecto secundario; es el objetivo.',
  },

  nombres: [
    {
      titulo: 'Parentalidad positiva',
      parrafos: [
        'Es el término institucional en España y en Europa. Lo define una recomendación del Consejo de Europa de 2006 y lo usan la administración y los colegios profesionales. Describe una crianza basada en afecto, estructura, reconocimiento, capacitación y ausencia de violencia.',
        'Es el nombre que encaja con lo que hace esta app, y el que un profesional reconoce sin pedir explicaciones. Cuando hace falta un solo término, este es el término.',
      ],
    },
    {
      titulo: 'Disciplina positiva: las herramientas sí, la etiqueta no',
      parrafos: [
        '«Disciplina positiva» designa algo concreto: el marco de Jane Nelsen, heredero de Adler y Dreikurs. Una de sus tesis centrales es que premios y castigos son la misma moneda por las dos caras, porque ambos son control externo. Nelsen se opone de forma explícita a los sistemas de puntos.',
        'En el habla corriente el término se ha vuelto un paraguas para «criar sin castigar», y en ese sentido flojo nadie objeta. Ante alguien que haya estudiado el marco, en cambio, la etiqueta se cae.',
        'Aquí las dos cosas van separadas. Las herramientas se aplican enteras —amable y firme a la vez, aliento en lugar de alabanza, reglas dichas de antemano, reunión semanal, nunca restar— y el nombre no se usa, porque debajo hay una economía de fichas y eso Nelsen no lo firmaría.',
      ],
    },
    {
      titulo: 'Economía de fichas con desvanecimiento',
      parrafos: [
        'Es el nombre exacto del mecanismo, y el que entiende alguien del oficio. Consiste en ganar fichas por conductas concretas y cambiarlas por un menú de recompensas. Tiene evidencia sólida desde los años sesenta y se usa de forma habitual con niños con TEA y con TDAH.',
        'El desvanecimiento es la parte que suele faltar en los sistemas caseros: aquí cada tarea sube por una escalera de cuatro peldaños y, a medida que se domina, paga menos, hasta dejar de pagar. Sin esa retirada programada, una economía de fichas crea dependencia de la ficha.',
      ],
    },
    {
      titulo: 'Apoyo conductual positivo',
      parrafos: [
        'Es el marco que corresponde a las adaptaciones para un niño con TEA: estructurar el entorno para que lo que toca sea lo fácil, enseñar habilidades en lugar de corregir conductas, y medir el resultado en calidad de vida y no en obediencia.',
        'De ahí vienen los apoyos visuales, las tareas partidas en pasos cortos y la foto de «así queda hecho», que sustituye una instrucción ambigua por un criterio que el niño puede comprobar sin preguntar a nadie.',
      ],
    },
  ] as readonly Bloque[],

  decirle: {
    titulo: 'Cómo se explica, según a quién',
    ayuda:
      'En cada caso, primero la versión que se entiende bien, y debajo el punto por donde la conversación suele torcerse.',
    filas: [
      {
        quien: 'En el colegio',
        decir:
          'Parentalidad positiva, con apoyos visuales y rutinas estructuradas en casa. Encaja con el vocabulario que ya se usa en las adaptaciones.',
        evitar:
          'La idea de que a los niños se les paga por las tareas. Es lo que peor se entiende, y además no describe el sistema: las rutinas no dan monedas.',
      },
      {
        quien: 'Ante quien acompaña el TEA',
        decir:
          'Economía de fichas con desvanecimiento, dentro de un marco de apoyo conductual positivo. Los apoyos son visuales y se retiran por peldaños.',
        evitar:
          'Presentarlo como algo que sustituye su criterio. Es una herramienta doméstica, y funciona mejor cuando el equipo sabe que existe.',
      },
      {
        quien: 'Entre los adultos de la casa',
        decir:
          'Un andamio para que los niños aprendan a hacer las cosas solos. Lo que ya saben hacer deja de dar monedas.',
        evitar:
          'La etiqueta «disciplina positiva» ante alguien que la haya estudiado. Abre una discusión sobre el nombre que no cambia nada del sistema.',
      },
      {
        quien: 'Ante quien lo ve como un soborno',
        decir:
          'Lo que hay que hacer por vivir en una casa no se paga: eso son las rutinas, y no dan monedas. Se paga el trabajo extra, y cada vez menos según se domina.',
        evitar:
          'Discutir sobre teoría. La objeción es legítima y la respuesta está en el diseño, no en la bibliografía: la diferencia entre rutina y misión existe justo para eso.',
      },
      {
        quien: 'A los propios niños',
        decir:
          'Sirve para aprender a llevar una casa sin que nadie tenga que recordarlo. A los ocho y a los diez años esto se entiende sin más explicación.',
        evitar:
          'Presentarlo como un premio por portarse bien. No va de conducta; va de aprender algo y de dejar de necesitar ayuda para hacerlo.',
      },
    ] as readonly Situacion[],
  },

  senal: {
    titulo: 'Cómo se sabe si está funcionando',
    parrafos: [
      'La medida que importa no es cuántas misiones se completan ni cuántas monedas se ahorran. Es cuánto aparece que nadie ha pagado: ponerse a cocinar con un adulto, recoger algo que no estaba en la lista, avisar de que se ha acabado el pan.',
      'Eso no lo produce la economía de fichas. Lo produce haber contribuido varias semanas seguidas y que se note. Adler lo llamaba pertenencia y significación, y es el resultado que se buscaba desde el principio.',
      'Cuando además baja el consumo de pantalla, rara vez es por haberla prohibido. Suele ser porque ha aparecido algo que compite con ella y que tiene sitio propio en la casa.',
    ],
  },

  mates: {
    titulo: 'Cómo enseña el repaso de mates',
    parrafos: [
      'El repaso no es una calculadora que dice si está bien o no. Cuando una cuenta no sale, la enseña hecha como en el cuaderno: la suma en columna con lo que se lleva, la resta de «de ocho a trece van cinco», la multiplicación con sus filas corridas y la división en su caja, con el resto debajo y la cifra que se baja. El mismo formato que en clase, para que lo que se aprende aquí se reconozca allí.',
      'La ayuda se retira sola. Tras un fallo, el niño ve la cuenta hecha paso a paso y, justo después, hace una parecida de la que solo contesta el último paso. Con cada acierto contesta un paso más, hasta hacerla entera; y entonces vuelve a preguntarse sin pasos, como a cualquiera. Si tropieza, la ayuda vuelve. Es lo que la investigación sobre ejemplos resueltos llama desvanecimiento hacia atrás, y es la forma que mejor funciona en los estudios: ver el camino entero antes de tener que andarlo.',
      'En cada paso hay dos pistas, de lo general a lo concreto, y la respuesta solo se enseña después de dos intentos. No está a un botón porque, cuando lo está, los alumnos la pulsan sin leer las pistas. Lo hecho con ayuda queda anotado aparte: cuenta como practicado, no como sabido, y por eso esa cuenta vuelve a salir.',
      'La base está en Atkinson, Derry, Renkl y Wortham (2000), la revisión del programa de investigación de los ejemplos resueltos; en Renkl y Atkinson (2003) y Atkinson, Renkl y Merrill (2003), sobre el desvanecimiento de los pasos; y en Koedinger y Aleven (2007), sobre el dilema de cuánta ayuda dar y qué pasa con las pistas que dan la respuesta.',
    ],
  },

  aviso: {
    titulo: 'Lo que estropea un sistema así',
    parrafos: [
      'Las primeras semanas van mejor de lo que irá después. Es lo habitual: la novedad y la estructura clara dan un empujón que se agota. El examen de verdad llega entre la sexta y la décima semana, y por eso conviene observar desde el principio.',
      'El riesgo concreto es que las monedas se conviertan en el motivo. Cuando cocinar en compañía empieza a depender de si eso paga, se ha perdido justo lo que se había ganado.',
    ],
    reglas: [
      'Lo que aparece solo no se paga. Se nombra, que es distinto: meterlo en la tienda lo convierte en trabajo.',
      'Y se nombra describiendo, no juzgando. Se dice lo que el niño ha hecho y qué efecto tuvo, en vez de decir cómo es. Eso es aliento en lugar de alabanza, y es la diferencia entre reconocer un esfuerzo y colgar una etiqueta.',
    ],
  },
} as const
